/* eslint-disable react-refresh/only-export-components -- intentionally co-locates pure helpers/constants with the presentational pieces they back; matches the same tradeoff already made in references-context.tsx et al. */
/**
 * Pure helpers + presentational pieces shared between the legacy per-host
 * `useSelectionToolbar` hook (still used by `EditableCell`) and the
 * multi-host `SelectionToolbarProvider` (used by `EditableText`). Extracted
 * so the actual toolbar UI/behavior — buttons, color/font-size palettes,
 * keyboard/outside-click plumbing — lives in exactly one place and can't
 * drift between the two call sites.
 */
import { useEffect, useRef } from 'react';

import {
  DEFAULT_COLOR_PALETTE,
  applyColorToRange,
  dispatchInputEventOnEditableHost,
  getLastPickedColor,
  getSelectionColor,
  removeColorFromRange,
} from '@/lib/text-color-ops';
import {
  DEFAULT_FONT_SIZE_PALETTE,
  applyFontSizeToRange,
  getLastPickedFontSize,
  getSelectionFontSize,
  removeFontSizeFromRange,
} from '@/lib/text-font-size-ops';
import { getChipFromRange } from '@/lib/resolve-edit-link-open';

import './use-selection-toolbar.scss';

export const DEFAULT_SWATCH_BACKGROUND = '#28a745';
export const DEFAULT_SIZE_LABEL = 'A';

export const TOOLBAR_OFFSET = 8;
export const TOOLBAR_HEIGHT_APPROX = 42;

export type SelRect = {
  top: number;
  left: number;
};

/** Live selection confined entirely inside `host`, excluding chip-only selections. */
export function selectionInsideHost(host: HTMLElement): Range | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  const inside = (node: Node | null): boolean =>
    node !== null && host.contains(node);
  if (!inside(range.startContainer) || !inside(range.endContainer)) return null;
  if (getChipFromRange(range)) return null;
  return range;
}

export function applyCommand(cmd: 'bold' | 'italic' | 'underline'): void {
  const anchor = window.getSelection()?.anchorNode ?? null;
  document.execCommand(cmd);
  if (anchor) dispatchInputEventOnEditableHost(anchor);
}

export function isInsideToolbar(node: Node): boolean {
  let cur: Node | null = node;
  while (cur) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).hasAttribute('data-selection-toolbar')
    ) {
      return true;
    }
    cur = cur.parentNode;
  }
  return false;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function ReferenceButton({ onPress }: { onPress: () => void }) {
  // Build the chain-link SVG once per mount. Mirrors the icon used by the
  // EditorJS ReferenceTool so the cluster and EditableText paths look
  // visually identical to the user.
  const svgRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    const host = svgRef.current;
    if (!host || host.childNodes.length > 0) return;
    host.setAttribute('viewBox', '0 0 24 24');
    host.setAttribute('fill', 'none');
    host.setAttribute('stroke', 'currentColor');
    host.setAttribute('stroke-width', '2');
    host.setAttribute('stroke-linecap', 'round');
    host.setAttribute('stroke-linejoin', 'round');
    const p1 = document.createElementNS(SVG_NS, 'path');
    p1.setAttribute(
      'd',
      'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
    );
    const p2 = document.createElementNS(SVG_NS, 'path');
    p2.setAttribute(
      'd',
      'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
    );
    host.appendChild(p1);
    host.appendChild(p2);
  }, []);
  return (
    <button
      type="button"
      className="selection-toolbar__button"
      aria-label="Insert reference"
      title="Insert reference"
      onMouseDown={e => {
        e.preventDefault();
        onPress();
      }}
    >
      <svg ref={svgRef} width="14" height="14" />
    </button>
  );
}

function ToolbarButton({
  label,
  cmd,
  short,
}: {
  label: string;
  cmd: 'bold' | 'italic' | 'underline';
  short: string;
}) {
  return (
    <button
      type="button"
      className={`selection-toolbar__button selection-toolbar__button--${cmd}`}
      aria-label={label}
      title={`${label} (${short})`}
      onMouseDown={e => {
        // mousedown not click: prevents the button from stealing focus and
        // clearing the contentEditable selection before the command runs.
        e.preventDefault();
        applyCommand(cmd);
      }}
    >
      {label.charAt(0)}
    </button>
  );
}

function ColorButton({
  getRange,
  refreshRange,
  activeColor,
  isOpen,
  onOpenChange,
}: {
  getRange: () => Range | null;
  // `applyColorToRange`/`removeColorFromRange` collapse whatever range
  // they're called on (`Range#extractContents` side effect) and reselect the
  // mutated contents as the live browser selection. `rangeRef` isn't
  // refreshed by the caller's own `selectionchange` listener while the
  // palette is open (deliberately, to avoid tearing the palette down mid-
  // pick) — so callers must explicitly pull the fresh live selection into
  // `rangeRef` after every mutation, or the NEXT swatch/reset/picker click
  // in the same session would act on a stale, now-collapsed range and
  // silently no-op.
  refreshRange: () => void;
  // Swatch background — should reflect the current selection's color (when
  // it's inside a color span) or the user's last pick, falling through to a
  // neutral default. Computed by the caller so this child stays pure.
  activeColor: string;
  // The caller owns the palette-open state so it can also be closed via
  // outside-the-component triggers like the Esc keydown handler.
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Apply-on-click semantics: every pick mutates the DOM immediately. The
  // palette stays open until the user explicitly Confirms (or Esc, or
  // clicks outside the toolbar entirely). Iteration via picking different
  // swatches just overrides the previous span; Reset clears.
  return (
    <span className="selection-toolbar__color">
      <button
        type="button"
        className="selection-toolbar__button selection-toolbar__button--color"
        aria-label="Text color"
        title="Text color"
        onMouseDown={e => {
          // mousedown not click so the host selection isn't lost when the
          // button takes focus. Toggle the popup rather than open-only so
          // a second click on the swatch closes it.
          e.preventDefault();
          onOpenChange(!isOpen);
        }}
      >
        <span
          className="selection-toolbar__color-swatch"
          style={{ background: activeColor }}
        />
      </button>
      {isOpen && (
        <div
          className="selection-toolbar__palette"
          role="listbox"
          aria-label="Color palette"
          data-selection-toolbar
        >
          <div className="selection-toolbar__palette-row">
            {DEFAULT_COLOR_PALETTE.map(c => (
              <button
                key={c}
                type="button"
                className="selection-toolbar__swatch"
                aria-label={`Apply color ${c}`}
                title={c}
                style={{ background: c }}
                onMouseDown={e => {
                  e.preventDefault();
                  const range = getRange();
                  if (range && applyColorToRange(range, c)) refreshRange();
                  // Palette stays open so the user can preview another
                  // colour or commit via Confirm.
                }}
              />
            ))}
          </div>
          <div className="selection-toolbar__palette-row selection-toolbar__palette-row--actions">
            <button
              type="button"
              className="selection-toolbar__palette-action"
              aria-label="Reset color"
              title="Reset color"
              onMouseDown={e => {
                // Pass the user's captured range explicitly — reading the
                // live Selection here would race with focus changes that
                // the click can cause in some browsers (and in jsdom).
                e.preventDefault();
                const range = getRange();
                if (range && removeColorFromRange(range)) refreshRange();
              }}
            >
              {/* Circle-with-slash — visually communicates "no color" */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="5" y1="19" x2="19" y2="5" />
              </svg>
            </button>
            <label
              className="selection-toolbar__palette-action selection-toolbar__palette-action--picker"
              title="Custom color"
            >
              <span aria-hidden="true">＋</span>
              <span className="selection-toolbar__visually-hidden">
                Pick custom color
              </span>
              <input
                type="color"
                aria-label="Custom color"
                className="selection-toolbar__color-input"
                // Hex from <input type="color"> is always #rrggbb lowercase
                // per spec. We still defer to applyColorToRange's validation.
                onMouseDown={e => {
                  // Native picker opens on click; don't let the parent
                  // mousedown handler steal focus from the host.
                  e.stopPropagation();
                }}
                onChange={e => {
                  const range = getRange();
                  if (range && applyColorToRange(range, e.target.value)) {
                    refreshRange();
                  }
                }}
              />
            </label>
            <button
              type="button"
              className="selection-toolbar__palette-action selection-toolbar__palette-action--confirm"
              aria-label="Confirm color selection"
              title="Confirm"
              onMouseDown={e => {
                e.preventDefault();
                onOpenChange(false);
              }}
            >
              {/* Checkmark — committing the current state. */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="4 12 10 18 20 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

function FontSizeButton({
  getRange,
  refreshRange,
  activeLabel,
  isOpen,
  onOpenChange,
}: {
  getRange: () => Range | null;
  // See the identical `refreshRange` doc on `ColorButton` — same underlying
  // stale-range bug, same fix.
  refreshRange: () => void;
  // Button label — should reflect the current selection's size (when it's
  // inside a font-size span) or the user's last pick, falling through to a
  // neutral default. Computed by the caller so this child stays pure.
  activeLabel: string;
  // The caller owns the palette-open state so it can also be closed via
  // outside-the-component triggers like the Esc keydown handler.
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Apply-on-click semantics: every pick mutates the DOM immediately. The
  // palette stays open until the user explicitly Confirms (or Esc, or
  // clicks outside the toolbar entirely). Iteration via picking different
  // sizes just overrides the previous span; Reset clears.
  return (
    <span className="selection-toolbar__font-size">
      <button
        type="button"
        className="selection-toolbar__button selection-toolbar__button--font-size"
        aria-label="Font size"
        title="Font size"
        onMouseDown={e => {
          // mousedown not click so the host selection isn't lost when the
          // button takes focus. Toggle the popup rather than open-only so
          // a second click closes it.
          e.preventDefault();
          onOpenChange(!isOpen);
        }}
      >
        {activeLabel}
      </button>
      {isOpen && (
        <div
          className="selection-toolbar__palette"
          role="listbox"
          aria-label="Font size palette"
          data-selection-toolbar
        >
          <div className="selection-toolbar__palette-row selection-toolbar__palette-row--wrap">
            {DEFAULT_FONT_SIZE_PALETTE.map(pt => (
              <button
                key={pt}
                type="button"
                className="selection-toolbar__size-chip"
                aria-label={`Apply font size ${pt}pt`}
                title={`${pt}pt`}
                onMouseDown={e => {
                  e.preventDefault();
                  const range = getRange();
                  if (range && applyFontSizeToRange(range, pt)) refreshRange();
                  // Palette stays open so the user can preview another
                  // size or commit via Confirm.
                }}
              >
                {pt}
              </button>
            ))}
          </div>
          <div className="selection-toolbar__palette-row selection-toolbar__palette-row--actions">
            <button
              type="button"
              className="selection-toolbar__palette-action"
              aria-label="Reset font size"
              title="Reset font size"
              onMouseDown={e => {
                // Pass the user's captured range explicitly — reading the
                // live Selection here would race with focus changes that
                // the click can cause in some browsers (and in jsdom).
                e.preventDefault();
                const range = getRange();
                if (range && removeFontSizeFromRange(range)) refreshRange();
              }}
            >
              {/* Circle-with-slash — visually communicates "default size" */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="5" y1="19" x2="19" y2="5" />
              </svg>
            </button>
            <button
              type="button"
              className="selection-toolbar__palette-action selection-toolbar__palette-action--confirm"
              aria-label="Confirm font size selection"
              title="Confirm"
              onMouseDown={e => {
                e.preventDefault();
                onOpenChange(false);
              }}
            >
              {/* Checkmark — committing the current state. */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="4 12 10 18 20 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

export type SelectionToolbarButtonsProps = {
  /** Omitted entirely when the host didn't register a reference handler. */
  onAddReference?: () => void;
  getRange: () => Range | null;
  refreshRange: () => void;
  colorPaletteOpen: boolean;
  onColorPaletteOpenChange: (open: boolean) => void;
  fontSizePaletteOpen: boolean;
  onFontSizePaletteOpenChange: (open: boolean) => void;
};

/** The actual row of formatting controls — shared markup, no positioning/portal. */
export function SelectionToolbarButtons({
  onAddReference,
  getRange,
  refreshRange,
  colorPaletteOpen,
  onColorPaletteOpenChange,
  fontSizePaletteOpen,
  onFontSizePaletteOpenChange,
}: SelectionToolbarButtonsProps) {
  return (
    <>
      <ToolbarButton label="Bold" cmd="bold" short="⌘B" />
      <ToolbarButton label="Italic" cmd="italic" short="⌘I" />
      <ToolbarButton label="Underline" cmd="underline" short="⌘U" />
      {onAddReference && <ReferenceButton onPress={onAddReference} />}
      <ColorButton
        getRange={getRange}
        refreshRange={refreshRange}
        // Priority: the color CURRENTLY APPLIED at the selection's start, then
        // the user's most recent pick (module-level memory), then a neutral
        // default. Re-computed each render — the toolbar re-renders on every
        // selection move, so the swatch tracks the caret as it moves between
        // colored runs.
        activeColor={
          getSelectionColor() ?? getLastPickedColor() ?? DEFAULT_SWATCH_BACKGROUND
        }
        isOpen={colorPaletteOpen}
        onOpenChange={onColorPaletteOpenChange}
      />
      <FontSizeButton
        getRange={getRange}
        refreshRange={refreshRange}
        // Priority: the size CURRENTLY APPLIED at the selection's start, then
        // the user's most recent pick (module-level memory), then a neutral
        // default label. Re-computed each render — see the color swatch
        // comment above.
        activeLabel={String(
          getSelectionFontSize() ??
            getLastPickedFontSize() ??
            DEFAULT_SIZE_LABEL,
        )}
        isOpen={fontSizePaletteOpen}
        onOpenChange={onFontSizePaletteOpenChange}
      />
    </>
  );
}

/** Toolbar's fixed `top` given the anchor rect — flips below when it wouldn't fit above the viewport top. */
export function computeToolbarTop(selRectTop: number): number {
  const wouldFitAbove =
    selRectTop - TOOLBAR_HEIGHT_APPROX - TOOLBAR_OFFSET >= 0;
  return wouldFitAbove
    ? selRectTop - TOOLBAR_HEIGHT_APPROX - TOOLBAR_OFFSET
    : selRectTop + 20;
}
